import useSWR from 'swr'

import {
  fetchOrderConfirmationBanners,
  type OrderConfirmationBanner,
  type OrderConfirmationRafStepperData,
  type OrderConfirmationRafSuccessMessage,
} from '@library/api-customer-engagement-experience'

export type ValidatedRafBannerData = OrderConfirmationRafStepperData & {
  action: OrderConfirmationRafStepperData['action'] & {
    successMessage: OrderConfirmationRafSuccessMessage
  }
}

export type UseRafBannerResult =
  | { status: 'loading' }
  | { status: 'none' }
  | { status: 'banner'; data: ValidatedRafBannerData }

// Without a successMessage the CTA copy can't render — treated as no banner rather than
// hardcoding copy client-side.
const hasSuccessMessage = (
  item: OrderConfirmationBanner,
): item is OrderConfirmationBanner & { data: ValidatedRafBannerData } =>
  item.type === 'raf_stepper' && Boolean(item.data.action.successMessage)

// Fetch/parse failures fold into 'none' like empty data — this endpoint requires total silence
// on failure.
export const useRafBanner = (orderId: string): UseRafBannerResult => {
  // Namespaced key avoids colliding with useOrderDeliveryDetails' bare-orderId cache entry; null
  // key skips the fetch while orderId is the placeholder `false` OrderConfirmationContainer
  // passes before the order loads; shouldRetryOnError avoids swapping out an already-shown
  // fallback if a later background retry succeeds.
  const { data, error } = useSWR(
    orderId ? ['order-confirmation-banners', orderId] : null,
    ([, id]) => fetchOrderConfirmationBanners(id),
    { shouldRetryOnError: false },
  )
  const banner = data?.find(hasSuccessMessage)

  if (error) {
    return { status: 'none' }
  }

  if (!data) {
    return { status: 'loading' }
  }

  return banner ? { status: 'banner', data: banner.data } : { status: 'none' }
}
